import { CheckCircleFilled, VideoCameraOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, DatePicker, Empty, Flex, Form, Input, InputNumber, List, Modal, Radio, Result, Row, Select, Space, Table, Tag, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useActiveWarehouse } from "@/lib/useWarehouses";
import { UnitsTable } from "../aftersales/CaseDetail";
import { CaseKindTag, CaseStatusTag, GRADES, type Case, type Grade } from "../aftersales/common";
import { uploadCaseVideo, usePackingRecorder, type Recorder } from "./recorder";

type Lookup = { cases: Case[]; order: Case["order"] };

/**
 * Gudang > Terima retur (Plan §6.10): scan what is on the parcel (the return
 * tracking number, the outbound one, or the order number), unbox it on camera,
 * grade every item, finish. Works in every warehouse, with or without WMS.
 */
export function Returns() {
  const { t } = useI18n();
  // The returns dock of the active warehouse (Plan §6.14).
  const { current } = useActiveWarehouse();
  const [caseId, setCaseId] = useUrlFilter<string>("case", "");
  const recording = !!current?.recordPacking;
  const rec = usePackingRecorder(recording, current?.name ?? "");

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("as.dock.title")}
        </Typography.Title>
      </Flex>
      {recording && <CameraStrip rec={rec} />}
      {current && (caseId ? <Desk caseId={caseId} warehouseId={current.id} wms={current.wmsEnabled} rec={recording ? rec : null} onClose={() => setCaseId("")} /> : <Find warehouseId={current.id} onOpen={setCaseId} />)}
    </>
  );
}

function CameraStrip({ rec }: { rec: Recorder }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = rec.stream;
  }, [rec.stream]);
  if (rec.error) return <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("wms.video.noCamera")} description={t("wms.video.noCameraHelp")} />;
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Flex gap={12} align="center" wrap>
        <video ref={ref} muted autoPlay playsInline style={{ width: 160, height: 90, background: "black", borderRadius: 4, objectFit: "cover" }} />
        <Space orientation="vertical" size={2}>
          <Space>
            <VideoCameraOutlined />
            {rec.recording ? <Tag color="red">● REC</Tag> : <Tag>{t(rec.ready ? "wms.video.standby" : "wms.video.starting")}</Tag>}
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("as.dock.videoHelp")}
          </Typography.Text>
        </Space>
      </Flex>
    </Card>
  );
}

/** Scanning a parcel: its case, or the order to open one for, or an unidentified parcel. */
function Find({ warehouseId, onOpen }: { warehouseId: string; onOpen: (id: string) => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const tz = me?.workspace?.timezone;
  const [code, setCode] = useState("");
  const [found, setFound] = useState<Lookup | null>(null);
  const [unknownOpen, setUnknownOpen] = useState(false);
  const [form] = Form.useForm<{ trackingNo?: string; carrier?: string; note?: string }>();
  const inProgress = useQuery({
    queryKey: ["aftersales", "list", { status: "receiving", warehouseId }],
    queryFn: () => unwrap(api.GET("/v1/aftersales", { params: { query: { status: "receiving", warehouseId } } })),
  });
  const lookup = useMutation({
    mutationFn: (c: string) => unwrap(api.GET("/v1/aftersales/lookup", { params: { query: { code: c } } })),
    onSuccess: (res, c) => {
      setCode(c);
      const only = res.cases.length === 1 ? res.cases[0] : undefined;
      if (only) {
        onOpen(only.id);
        return;
      }
      setFound(res);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const arrive = useMutation({
    mutationFn: (body: { orderId?: string; kind?: "failed_delivery" | "return_refund"; trackingNo?: string; carrier?: string; note?: string }) =>
      unwrap(api.POST("/v1/aftersales/arrivals", { body: { warehouseId, ...body } })),
    onSuccess: (c) => {
      setUnknownOpen(false);
      onOpen(c.id);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={14}>
        <Card size="small" title={t("as.dock.scanTitle")}>
          <ScanInput onScan={(c) => lookup.mutateAsync(c).then((res) => res.cases.length > 0)} placeholder={t("as.dock.scan")} />
          <div style={{ marginTop: 16 }}>
            {found && found.cases.length > 1 && (
              <List
                header={<Typography.Text strong>{t("as.dock.pick")}</Typography.Text>}
                dataSource={found.cases}
                renderItem={(c) => (
                  <List.Item actions={[<Button key="open" type="primary" onClick={() => onOpen(c.id)}>{t("as.dock.open")}</Button>]}>
                    <Space size={6} wrap>
                      <Typography.Text strong className="z-num">
                        {c.number}
                      </Typography.Text>
                      <CaseKindTag kind={c.kind} />
                      <Typography.Text type="secondary">{c.orderNumber}</Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            )}
            {found && found.cases.length === 0 && found.order && (
              <Alert
                type="info"
                showIcon
                title={t("as.dock.orderNoCase", { number: found.order.number })}
                description={t("as.dock.orderNoCaseHelp")}
                action={
                  <Space orientation="vertical">
                    <Button type="primary" block loading={arrive.isPending} onClick={() => arrive.mutate({ orderId: found.order!.id, kind: "failed_delivery", trackingNo: code })}>
                      {t("as.dock.asFailed")}
                    </Button>
                    <Button block loading={arrive.isPending} onClick={() => arrive.mutate({ orderId: found.order!.id, kind: "return_refund", trackingNo: code })}>
                      {t("as.dock.asReturn")}
                    </Button>
                  </Space>
                }
              />
            )}
            {found && found.cases.length === 0 && !found.order && (
              <Alert
                type="warning"
                showIcon
                title={t("as.dock.notFound", { code })}
                action={
                  <Button
                    onClick={() => {
                      form.setFieldsValue({ trackingNo: code, carrier: "", note: "" });
                      setUnknownOpen(true);
                    }}
                  >
                    {t("as.dock.unknown")}
                  </Button>
                }
              />
            )}
          </div>
        </Card>
      </Col>
      <Col xs={24} xl={10}>
        <Card size="small" title={t("as.dock.inProgress")}>
          <List
            size="small"
            loading={inProgress.isPending}
            dataSource={inProgress.data?.items ?? []}
            locale={{ emptyText: t("as.dock.noneInProgress") }}
            renderItem={(c) => (
              <List.Item style={{ cursor: "pointer" }} onClick={() => onOpen(c.id)}>
                <Space orientation="vertical" size={0}>
                  <Space size={6}>
                    <Typography.Text strong className="z-num">
                      {c.number}
                    </Typography.Text>
                    <CaseKindTag kind={c.kind} />
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {c.orderNumber || c.returnTrackingNo || "—"} · {formatDateTime(c.createdAt, tz)}
                  </Typography.Text>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      </Col>
      <Modal
        open={unknownOpen}
        title={t("as.dock.unknown")}
        onCancel={() => setUnknownOpen(false)}
        onOk={() => form.submit()}
        okText={t("as.dock.startUnboxing")}
        cancelText={t("common.cancel")}
        confirmLoading={arrive.isPending}
      >
        <Typography.Paragraph type="secondary">{t("as.dock.unknownHelp")}</Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={(v) => arrive.mutate({ trackingNo: v.trackingNo ?? "", carrier: v.carrier ?? "", note: v.note ?? "" })}>
          <Form.Item name="trackingNo" label={t("as.dock.tracking")}>
            <Input maxLength={64} className="z-num" />
          </Form.Item>
          <Form.Item name="carrier" label={t("as.returnCarrier")}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="note" label={t("as.note")}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
}

type Pending = { code: string; need: "serial" | "lot" };

/** Unboxing one case: CS instructions on the left, scanning and grading on the right. */
function Desk({ caseId, warehouseId, wms, rec, onClose }: { caseId: string; warehouseId: string; wms: boolean; rec: Recorder | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [grade, setGrade] = useState<Grade>("good");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  // A returned IMEI that did not leave with the order: taken in only with a reason.
  const [overriding, setOverriding] = useState<Parameters<typeof add.mutate>[0] | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [notOurs, setNotOurs] = useState(false);
  const [finished, setFinished] = useState<Case | null>(null);
  const [videoFailed, setVideoFailed] = useState<null | (() => void)>(null);
  const [extra] = Form.useForm<{ serial?: string; lotNo?: string; expiresOn?: Dayjs }>();
  const [other] = Form.useForm<{ description: string; quantity: number }>();
  const path = { params: { path: { caseID: caseId } } };
  const q = useQuery({ queryKey: ["aftersales", "case", caseId], queryFn: () => unwrap(api.GET("/v1/aftersales/{caseID}", path)) });
  const c = q.data;
  const set = (x: Case) => {
    queryClient.setQueryData(["aftersales", "case", caseId], x);
    void queryClient.invalidateQueries({ queryKey: ["aftersales", "list"] });
  };

  // The unboxing is recorded from the moment the case is open at the desk.
  const ready = rec?.ready ?? false;
  const start = rec?.start;
  const discard = rec?.discard;
  const active = !!c && (c.status === "open" || c.status === "receiving");
  const label = c ? [c.number, c.orderNumber].filter(Boolean).join(" ") : "";
  useEffect(() => {
    if (ready && active && start && label) start(label);
  }, [ready, active, start, label]);
  useEffect(() => () => discard?.(), [discard]);
  const sendVideo = async () => {
    const seg = await rec?.stop();
    if (!seg) return;
    const send = () =>
      uploadCaseVideo(caseId, seg).then(
        () => {
          setVideoFailed(null);
          void queryClient.invalidateQueries({ queryKey: ["aftersales", "case", caseId] });
        },
        () => setVideoFailed(() => () => void send()),
      );
    await send();
  };

  const add = useMutation({
    mutationFn: (body: {
      code?: string;
      description?: string;
      grade: Grade;
      quantity: number;
      serials?: string[];
      serialOverride?: string;
      lot?: { no: string; expiresOn: string };
      note?: string;
    }) =>
      unwrap(api.POST("/v1/aftersales/{caseID}/units", { ...path, body: { warehouseId, ...body } })),
    onSuccess: (x) => {
      set(x);
      setPending(null);
      setOverriding(null);
      setOverrideReason("");
      setNotOurs(false);
      extra.resetFields();
      other.resetFields();
      setQty(1);
      setNote("");
    },
    onError: (err, body) => {
      if (err instanceof ApiError && err.code === "serial_not_shipped" && !body.serialOverride) {
        setPending(null);
        setOverriding(body);
        return;
      }
      if (err instanceof ApiError && body.code && (err.code === "serials_required" || err.code === "lot_required")) {
        setPending({ code: body.code, need: err.code === "serials_required" ? "serial" : "lot" });
        return;
      }
      void message.error(errorText(err));
    },
  });
  const remove = useMutation({
    mutationFn: (unitID: string) => unwrap(api.DELETE("/v1/aftersales/{caseID}/units/{unitID}", { params: { path: { caseID: caseId, unitID } } })),
    onSuccess: set,
    onError: (err) => void message.error(errorText(err)),
  });
  // Damaged units go to RUSAK, or another damaged location (e.g. one for goods waiting for a claim).
  const damagedPlaces = useQuery({
    queryKey: ["locations", warehouseId, "damaged"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId } } })),
    enabled: wms,
    select: (x) => x.items.filter((l) => l.kind === "damaged" && l.status === "active"),
  });
  const finish = useMutation({
    mutationFn: ({ n, dmg }: { n: string; dmg?: string }) =>
      unwrap(api.POST("/v1/aftersales/{caseID}/receive", { ...path, body: { note: n, ...(dmg ? { damagedLocationId: dmg } : {}) } })),
    onSuccess: (x) => {
      set(x);
      setFinished(x);
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void message.success(t("as.dock.finished", { number: x.number }));
      void sendVideo();
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (q.isError) return <Result status="error" title={errorText(q.error)} extra={<Button onClick={onClose}>{t("common.back")}</Button>} />;
  if (!c) return <Card loading />;
  if (finished) {
    return (
      <Result
        status="success"
        icon={<CheckCircleFilled />}
        title={t("as.dock.finished", { number: finished.number })}
        subTitle={wms ? t("as.dock.finishedWms") : t("as.dock.finishedLevel")}
        extra={[
          finished.putawayTaskId && (
            <Button key="pa" onClick={() => navigate(`/app/warehouse/tasks/${finished.putawayTaskId}`)}>
              {t("wms.in.openPutaway")}
            </Button>
          ),
          <Button key="next" type="primary" onClick={onClose}>
            {t("as.dock.next")}
          </Button>,
        ]}
      >
        {videoFailed && <Alert type="warning" showIcon title={t("as.dock.videoFailed")} action={<Button size="small" onClick={videoFailed}>{t("common.retry")}</Button>} />}
      </Result>
    );
  }
  const editable = c.status === "open" || c.status === "receiving";
  const submitScan = (code: string) => add.mutateAsync({ code, grade, quantity: qty, note });
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 12 }}>
        <Space size={8} wrap>
          <Typography.Title level={3} className="z-num" style={{ margin: 0, fontSize: 22 }}>
            {c.number}
          </Typography.Title>
          <CaseStatusTag status={c.status} />
          <CaseKindTag kind={c.kind} />
          {c.orderNumber && <Typography.Text type="secondary">{t("as.dock.order", { number: c.orderNumber })}</Typography.Text>}
        </Space>
        <Space>
          <Button
            onClick={() => {
              if (rec?.recording) void sendVideo();
              onClose();
            }}
          >
            {t("as.dock.leave")}
          </Button>
          {editable && (
            <Button
              type="primary"
              disabled={c.units.length === 0}
              loading={finish.isPending}
              onClick={() => {
                let n = "";
                let dmg: string | undefined;
                const damaged = c.units.some((u) => u.grade === "damaged" || u.grade === "incomplete");
                const places = damagedPlaces.data ?? [];
                modal.confirm({
                  title: t("as.dock.finish"),
                  content: (
                    <Space orientation="vertical" style={{ width: "100%" }}>
                      <Typography.Text>{wms ? t("as.dock.finishConfirmWms") : t("as.dock.finishConfirm")}</Typography.Text>
                      {wms && damaged && places.length > 1 && (
                        <Select<string>
                          style={{ width: "100%" }}
                          placeholder={t("as.dock.damagedPlace")}
                          allowClear
                          options={places.map((l) => ({ value: l.id, label: l.code }))}
                          onChange={(v) => (dmg = v)}
                        />
                      )}
                      <Input.TextArea rows={2} maxLength={2000} placeholder={t("as.dock.finishNote")} onChange={(e) => (n = e.target.value)} />
                    </Space>
                  ),
                  okText: t("as.dock.finish"),
                  cancelText: t("common.cancel"),
                  onOk: () => finish.mutateAsync({ n: n.trim(), dmg }),
                })
              }}
            >
              {t("as.dock.finish")}
            </Button>
          )}
        </Space>
      </Flex>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            <Card size="small" title={t("as.dock.instructions")}>
              {c.instructions || c.checks.length || c.reason ? (
                <Space orientation="vertical" size={8}>
                  {c.reason && <Typography.Text strong>{t(`as.reason.${c.reason}`)}</Typography.Text>}
                  {c.instructions && <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{c.instructions}</Typography.Paragraph>}
                  {c.checks.length > 0 && (
                    <Space size={4} wrap>
                      {c.checks.map((x) => (
                        <Tag key={x} color="orange">
                          {t(`as.check.${x}`)}
                        </Tag>
                      ))}
                    </Space>
                  )}
                </Space>
              ) : (
                <Typography.Text type="secondary">{t("as.dock.noInstructions")}</Typography.Text>
              )}
              {(c.buyerReason || c.buyerNote) && (
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  {t("as.buyerReason")}: {[c.buyerReason, c.buyerNote].filter(Boolean).join(" — ")}
                </Typography.Paragraph>
              )}
            </Card>
            <Card size="small" title={t("as.expected")}>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={c.lines}
                locale={{ emptyText: t("as.noLines") }}
                columns={[
                  {
                    title: t("as.item"),
                    render: (_, l) => (
                      <Space orientation="vertical" size={0}>
                        <Typography.Text strong className="z-num">
                          {l.skuCode || l.sellerSku}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {l.name}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  {
                    title: t("as.receivedCol"),
                    width: 110,
                    align: "right",
                    render: (_, l) => (
                      <Typography.Text strong className="z-num" type={l.received >= l.expected ? "success" : undefined}>
                        {l.received} / {l.expected}
                      </Typography.Text>
                    ),
                  },
                ]}
              />
            </Card>
          </Space>
        </Col>
        <Col xs={24} xl={14}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            {editable && (
              <Card size="small" title={t("as.dock.scanItem")}>
                <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                  <Radio.Group value={grade} onChange={(e) => setGrade(e.target.value as Grade)} optionType="button" buttonStyle="solid">
                    {GRADES.filter((g) => g !== "wrong_item").map((g) => (
                      <Radio.Button key={g} value={g}>
                        {t(`as.gradeShort.${g}`)}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`as.grade.${grade}`)}
                  </Typography.Text>
                  <Space wrap>
                    <InputNumber min={1} max={10000} precision={0} value={qty} onChange={(v) => setQty(v ?? 1)} prefix={t("as.dock.quantity")} style={{ width: 160 }} />
                    <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={t("as.dock.itemNote")} style={{ width: 280 }} />
                  </Space>
                  <ScanInput onScan={submitScan} placeholder={t("as.dock.scanItemPlaceholder")} disabled={add.isPending || !!pending} />
                  <Button onClick={() => setNotOurs(true)}>{t("as.dock.notOurs")}</Button>
                </Space>
              </Card>
            )}
            <Card size="small" title={t("as.dock.items")}>
              <UnitsTable units={c.units} onRemove={c.status === "receiving" ? (u) => remove.mutate(u.id) : undefined} />
            </Card>
          </Space>
        </Col>
      </Row>
      <Modal
        open={!!overriding}
        title={t("serial.return.title", { serial: overriding?.serials?.[0] ?? "" })}
        okText={t("serial.return.ok")}
        okButtonProps={{ danger: true, disabled: overrideReason.trim().length < 3 }}
        cancelText={t("common.cancel")}
        confirmLoading={add.isPending}
        onCancel={() => setOverriding(null)}
        onOk={() => overriding && add.mutate({ ...overriding, serialOverride: overrideReason.trim() })}
        destroyOnHidden
      >
        <Typography.Paragraph type="warning">{t("serial.return.help")}</Typography.Paragraph>
        <Input.TextArea autoFocus rows={3} maxLength={300} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder={t("serial.return.placeholder")} />
      </Modal>
      <Modal
        open={!!pending}
        title={pending?.need === "serial" ? t("as.dock.serialTitle", { code: pending.code }) : t("as.dock.lotTitle", { code: pending?.code ?? "" })}
        onCancel={() => setPending(null)}
        onOk={() => extra.submit()}
        okText={t("common.add")}
        cancelText={t("common.cancel")}
        confirmLoading={add.isPending}
        destroyOnHidden
      >
        <Form
          form={extra}
          layout="vertical"
          onFinish={(v) =>
            pending &&
            add.mutate(
              pending.need === "serial"
                ? { code: pending.code, grade, quantity: 1, serials: [v.serial!.trim()], note }
                : { code: pending.code, grade, quantity: qty, note, lot: { no: v.lotNo!.trim(), expiresOn: v.expiresOn!.format("YYYY-MM-DD") } },
            )
          }
        >
          {pending?.need === "serial" ? (
            <Form.Item name="serial" label={t("as.dock.serial")} rules={[{ required: true, whitespace: true }]}>
              <Input autoFocus className="z-num" />
            </Form.Item>
          ) : (
            <>
              <Typography.Paragraph type="secondary">{t("as.dock.lotHelp")}</Typography.Paragraph>
              <Form.Item name="lotNo" label={t("as.dock.lotNo")} rules={[{ required: true, whitespace: true }]}>
                <Input autoFocus className="z-num" maxLength={64} />
              </Form.Item>
              <Form.Item name="expiresOn" label={t("as.dock.lotExpiry")} rules={[{ required: true }]}>
                <DatePicker format="DD/MM/YYYY" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
      <Modal
        open={notOurs}
        title={t("as.dock.notOurs")}
        onCancel={() => setNotOurs(false)}
        onOk={() => other.submit()}
        okText={t("common.add")}
        cancelText={t("common.cancel")}
        confirmLoading={add.isPending}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">{t("as.dock.notOursHelp")}</Typography.Paragraph>
        <Form form={other} layout="vertical" initialValues={{ quantity: 1 }} onFinish={(v) => add.mutate({ description: v.description.trim(), grade: "wrong_item", quantity: v.quantity, note })}>
          <Form.Item name="description" label={t("as.dock.notOursDesc")} rules={[{ required: true, whitespace: true }]}>
            <Input autoFocus maxLength={200} />
          </Form.Item>
          <Form.Item name="quantity" label={t("as.dock.quantity")}>
            <InputNumber min={1} max={10000} precision={0} />
          </Form.Item>
        </Form>
      </Modal>
      {!editable && <Empty description={t("as.dock.closed")} />}
    </>
  );
}
